import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Chip,
  IconButton,
  Switch,
  FormControlLabel,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  CurrencyRupee,
  DateRange,
  Autorenew,
  AccessTime,
} from '@mui/icons-material';
import api from '../../utils/api';
import { GRADIENTS } from '../../theme/tokens';

// Plan type configurations
const PLAN_TYPES = {
  daily: { label: 'Daily', duration: '1 Day', description: '1 Day access to the library' },
  weekly: { label: 'Weekly', duration: '7 Days', description: '7 Days access to the library' },
  monthly: { label: 'Monthly', duration: '1 Month', description: '1 Month (30 days) access to the library' },
  half_yearly: { label: 'Half-Yearly', duration: '6 Months', description: '6 Months (180 days) access to the library' },
};

// Valid hours per day options
const HOURS_OPTIONS = [
  { value: null, label: 'Unlimited' },
  { value: 2, label: '2 hours/day' },
  { value: 4, label: '4 hours/day' },
  { value: 6, label: '6 hours/day' },
  { value: 8, label: '8 hours/day' },
  { value: 10, label: '10 hours/day' },
  { value: 12, label: '12 hours/day' },
  { value: 14, label: '14 hours/day' },
  { value: 16, label: '16 hours/day' },
  { value: 18, label: '18 hours/day' },
  { value: 20, label: '20 hours/day' },
  { value: 22, label: '22 hours/day' },
  { value: 24, label: '24 hours/day' },
];

// First-time user discount options
const DISCOUNT_OPTIONS = [
  { value: 10, label: '10% off' },
  { value: 20, label: '20% off' },
  { value: 30, label: '30% off' },
  { value: 40, label: '40% off' },
  { value: 50, label: '50% off' },
];

function PlansManagement() {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Dialog states
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    plan_type: '',
    price_cents: '',
    recurring: false,
    hours_per_day: null,
    first_time_discount: null,
  });

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    try {
      setLoading(true);
      const response = await api.get('/librarian/plans');
      setPlans(response.data.data.plans);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch plans');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, checked, type } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value,
    });
  };

  const resetForm = () => {
    setFormData({
      plan_type: '',
      price_cents: '',
      recurring: false,
      hours_per_day: null,
      first_time_discount: null,
    });
  };

  const openCreateDialog = () => {
    resetForm();
    setCreateDialogOpen(true);
  };

  const openEditDialog = (plan) => {
    setSelectedPlan(plan);
    setFormData({
      plan_type: plan.plan_type || 'daily',
      price_cents: plan.price_cents,
      recurring: plan.recurring,
      hours_per_day: plan.hours_per_day,
      first_time_discount: plan.first_time_discount,
    });
    setEditDialogOpen(true);
  };

  const openDeleteDialog = (plan) => {
    setSelectedPlan(plan);
    setDeleteDialogOpen(true);
  };

  const handleCreate = async () => {
    try {
      setError('');
      setLoading(true);
      await api.post('/librarian/plans', {
        plan_type: formData.plan_type,
        price_cents: parseInt(formData.price_cents, 10),
        recurring: formData.recurring,
        hours_per_day: formData.hours_per_day,
        first_time_discount: formData.first_time_discount,
      });
      setSuccess('Plan created successfully!');
      setCreateDialogOpen(false);
      resetForm();
      fetchPlans();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create plan');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async () => {
    try {
      setError('');
      setLoading(true);
      await api.put(`/librarian/plans/${selectedPlan.id}`, {
        price_cents: parseInt(formData.price_cents, 10),
        recurring: formData.recurring,
        hours_per_day: formData.hours_per_day,
        first_time_discount: formData.first_time_discount,
      });
      setSuccess('Plan updated successfully!');
      setEditDialogOpen(false);
      resetForm();
      fetchPlans();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update plan');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      setError('');
      setLoading(true);
      await api.delete(`/librarian/plans/${selectedPlan.id}`);
      setSuccess('Plan deleted successfully!');
      setDeleteDialogOpen(false);
      setSelectedPlan(null);
      fetchPlans();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete plan');
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (rupees) => {
    return `₹${rupees}`;
  };

  // All plan types are available (librarians can create multiple plans of same type)
  const availablePlanTypes = Object.keys(PLAN_TYPES);

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h5" fontWeight="700" gutterBottom>
            Plans Management
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Create and manage subscription plans for your library
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={openCreateDialog}
          sx={{
            background: GRADIENTS.brand,
          }}
        >
          Add Plan
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      {plans.length === 0 ? (
        <Paper elevation={2} sx={{ p: 6, textAlign: 'center' }}>
          <DateRange sx={{ fontSize: 80, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No Plans Yet
          </Typography>
          <Typography variant="body2" color="text.secondary" mb={3}>
            Create your first plan to start offering subscriptions to students
          </Typography>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={openCreateDialog}
            sx={{
              background: GRADIENTS.brand,
            }}
          >
            Create First Plan
          </Button>
        </Paper>
      ) : (
        <TableContainer component={Paper} elevation={2}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell><strong>Plan Type</strong></TableCell>
                <TableCell><strong>Price</strong></TableCell>
                <TableCell><strong>Duration</strong></TableCell>
                <TableCell><strong>Hours/Day</strong></TableCell>
                <TableCell><strong>New User Discount</strong></TableCell>
                <TableCell><strong>Type</strong></TableCell>
                <TableCell align="right"><strong>Actions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {plans.map((plan) => (
                <TableRow key={plan.id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight="600">
                      {plan.name}
                      {plan.hours_per_day && (
                        <Typography component="span" variant="body2" color="info.main" sx={{ ml: 1 }}>
                          ({plan.hours_per_day}h/day)
                        </Typography>
                      )}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {plan.description}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <CurrencyRupee sx={{ fontSize: 18, color: 'success.main' }} />
                      <Typography variant="body2" fontWeight="600" color="success.main">
                        {formatPrice(plan.price_cents)}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <DateRange sx={{ fontSize: 18 }} />
                      <Typography variant="body2">
                        {plan.duration_days} days
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    {plan.hours_per_day ? (
                      <Chip
                        icon={<AccessTime sx={{ fontSize: 16 }} />}
                        label={`${plan.hours_per_day}h`}
                        size="small"
                        color="info"
                        variant="outlined"
                      />
                    ) : (
                      <Chip label="Unlimited" size="small" color="success" variant="outlined" />
                    )}
                  </TableCell>
                  <TableCell>
                    {plan.first_time_discount ? (
                      <Chip
                        label={`${plan.first_time_discount}% off`}
                        size="small"
                        color="warning"
                        sx={{ fontWeight: 600 }}
                      />
                    ) : (
                      <Typography variant="body2" color="text.secondary">-</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {plan.recurring ? (
                      <Chip
                        icon={<Autorenew />}
                        label="Recurring"
                        color="primary"
                        size="small"
                      />
                    ) : (
                      <Chip label="One-time" size="small" />
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Edit Price">
                      <IconButton
                        color="primary"
                        onClick={() => openEditDialog(plan)}
                        size="small"
                      >
                        <Edit fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton
                        color="error"
                        onClick={() => openDeleteDialog(plan)}
                        size="small"
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Create Plan Dialog */}
      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create New Plan</DialogTitle>
        <DialogContent>
          <Box mt={1}>
            <FormControl fullWidth margin="normal" required>
              <InputLabel>Plan Type</InputLabel>
              <Select
                name="plan_type"
                value={formData.plan_type}
                onChange={handleChange}
                label="Plan Type"
              >
                {availablePlanTypes.map((type) => (
                  <MenuItem key={type} value={type}>
                    {PLAN_TYPES[type].label} ({PLAN_TYPES[type].duration})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {formData.plan_type && (
              <Alert severity="info" sx={{ mt: 1, mb: 2 }}>
                {PLAN_TYPES[formData.plan_type]?.description}
              </Alert>
            )}

            <TextField
              fullWidth
              label="Price (₹)"
              name="price_cents"
              type="number"
              value={formData.price_cents}
              onChange={handleChange}
              required
              inputProps={{ min: 0 }}
              margin="normal"
              helperText="Enter amount in rupees (e.g., 100 for ₹100)"
            />

            <FormControl fullWidth margin="normal">
              <InputLabel>Daily Hours Limit</InputLabel>
              <Select
                name="hours_per_day"
                value={formData.hours_per_day === null ? 'unlimited' : formData.hours_per_day}
                onChange={(e) => {
                  const value = e.target.value === 'unlimited' ? null : e.target.value;
                  setFormData({ ...formData, hours_per_day: value });
                }}
                label="Daily Hours Limit"
              >
                {HOURS_OPTIONS.map((option) => (
                  <MenuItem key={option.value || 'unlimited'} value={option.value === null ? 'unlimited' : option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControlLabel
              control={
                <Switch
                  checked={formData.first_time_discount !== null}
                  onChange={(e) => {
                    setFormData({
                      ...formData,
                      first_time_discount: e.target.checked ? 10 : null
                    });
                  }}
                  color="warning"
                />
              }
              label="Offer discount for first-time users"
              sx={{ mt: 2 }}
            />

            {formData.first_time_discount !== null && (
              <FormControl fullWidth margin="normal">
                <InputLabel>Discount Percentage</InputLabel>
                <Select
                  value={formData.first_time_discount}
                  onChange={(e) => setFormData({ ...formData, first_time_discount: e.target.value })}
                  label="Discount Percentage"
                >
                  {DISCOUNT_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            <FormControlLabel
              control={
                <Switch
                  checked={formData.recurring}
                  onChange={handleChange}
                  name="recurring"
                  color="primary"
                />
              }
              label="Recurring Plan"
              sx={{ mt: 2 }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleCreate}
            variant="contained"
            disabled={loading || !formData.plan_type || !formData.price_cents}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Plan Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Plan</DialogTitle>
        <DialogContent>
          <Box mt={1}>
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="subtitle2">
                {selectedPlan?.name}
              </Typography>
              <Typography variant="body2">
                {selectedPlan?.description}
              </Typography>
            </Alert>

            <TextField
              fullWidth
              label="Price (₹)"
              name="price_cents"
              type="number"
              value={formData.price_cents}
              onChange={handleChange}
              required
              inputProps={{ min: 0 }}
              margin="normal"
              helperText="Enter amount in rupees"
            />

            <FormControl fullWidth margin="normal">
              <InputLabel>Daily Hours Limit</InputLabel>
              <Select
                name="hours_per_day"
                value={formData.hours_per_day === null ? 'unlimited' : formData.hours_per_day}
                onChange={(e) => {
                  const value = e.target.value === 'unlimited' ? null : e.target.value;
                  setFormData({ ...formData, hours_per_day: value });
                }}
                label="Daily Hours Limit"
              >
                {HOURS_OPTIONS.map((option) => (
                  <MenuItem key={option.value || 'unlimited'} value={option.value === null ? 'unlimited' : option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControlLabel
              control={
                <Switch
                  checked={formData.first_time_discount !== null}
                  onChange={(e) => {
                    setFormData({
                      ...formData,
                      first_time_discount: e.target.checked ? 10 : null
                    });
                  }}
                  color="warning"
                />
              }
              label="Offer discount for first-time users"
              sx={{ mt: 2 }}
            />

            {formData.first_time_discount !== null && (
              <FormControl fullWidth margin="normal">
                <InputLabel>Discount Percentage</InputLabel>
                <Select
                  value={formData.first_time_discount}
                  onChange={(e) => setFormData({ ...formData, first_time_discount: e.target.value })}
                  label="Discount Percentage"
                >
                  {DISCOUNT_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            <FormControlLabel
              control={
                <Switch
                  checked={formData.recurring}
                  onChange={handleChange}
                  name="recurring"
                  color="primary"
                />
              }
              label="Recurring Plan"
              sx={{ mt: 2 }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleUpdate} variant="contained" disabled={loading}>
            Update
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Plan</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the plan "{selectedPlan?.name}"?
          </Typography>
          <Alert severity="warning" sx={{ mt: 2 }}>
            This action cannot be undone. Plans with active bookings cannot be deleted.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained" disabled={loading}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default PlansManagement;
